import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Layers, Users, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import useScrollAnimation from '../hooks/useScrollAnimation';

import amazonLogo from '../assets/amazon.svg';
import noonLogo from '../assets/noon-2 2.svg';
import jumiaLogo from '../assets/jumia.svg';
import trendyolLogo from '../assets/trendyol.svg';

const marketplaces = [
  { src: amazonLogo, name: 'Amazon', width: 164, height: 51 },
  { src: noonLogo, name: 'Noon', width: 132, height: 51 },
  { src: jumiaLogo, name: 'Jumia', width: 104, height: 63 },
  { src: trendyolLogo, name: 'Trendyol', width: 620, height: 211 },
];
const ScaleCards = () => {
  const { language } = useLanguage();
  const t = translations[language] || {};
  const isAr = language === 'ar';
  const observe = useScrollAnimation();

  const title = t.scaleTitle || (isAr ? 'كل ما تحتاجه لتنمية أعمالك على منصات التجارة الإلكترونية' : 'Everything You Need To Scale Marketplaces');
  
  const card1Title = t.scaleCard1Title || (isAr ? 'منصة كونكتو' : 'Connecto Platform AI-Powered');
  const card1Desc = t.scaleCard1Desc || (isAr ? 'صُممت لتبسيط عمليات متجرك، بدءً من إنشاء صفحات المنتجات وحتى النشر المباشر على المنصات.' : "Built to support your team's workflow from product creation to marketplace publishing.");
  const card1Feats = [
    t.scaleCard1Feat1 || (isAr ? 'إنشاء صفحات منتجات بالذكاء الاصطناعي' : 'AI Product Listing Generation'),
    t.scaleCard1Feat2 || (isAr ? 'توليد محتوى احترافي لزيادة المبيعات' : 'High-Converting Content Creation'),
    t.scaleCard1Feat3 || (isAr ? 'حساب أرباح ورسوم المنصات قبل النشر' : 'Pre-Publish Profit & Fee Calculation'),
    t.scaleCard1Feat4 || (isAr ? 'لوحة تحكم موحدة لمتابعة الأداء' : 'Unified Performance Dashboard'),
  ];

  const card2Title = t.scaleCard2Title || (isAr ? 'إدارة حسابات المتاجر الإلكترونية' : 'Account Management');
  const card2Desc = t.scaleCard2Desc || (isAr ? 'فريق من الخبراء يتولى إدارة عمليات متجرك اليومية لتتفرغ لتطوير أعمالك.' : 'Daily operations handled by experts.');
  const card2Feats = [
    t.scaleCard2Feat1 || (isAr ? 'تسجيل وإعداد حسابات البائعين' : 'Seller Account Registration & Setup'),
    t.scaleCard2Feat2 || (isAr ? 'إنشاء القوائم وتطوير محتوى A+' : 'Listing Creation & A+ Content Development'),
    t.scaleCard2Feat3 || (isAr ? 'إدارة الحملات الإعلانية وتنمية المبيعات' : 'Ad Campaign Management & Sales Scaling'),
    t.scaleCard2Feat4 || (isAr ? 'متابعة الطلبات، المرتجعات، والعمليات المالية' : 'Order, Return & Financial Operation Tracking'),
  ];

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-white border-b border-edge-light" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-10 lg:mb-14">
          <span
            ref={observe}
            className="animate-fade-up inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-4"
          >
            {isAr ? 'الخدمات' : 'Services'}
          </span>
          <h2
            ref={observe}
            className="animate-fade-up font-ibm text-[26px] leading-[1.5] md:text-[34px] md:leading-[1.45] font-bold text-heading text-center"
          >
            {title}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {/* Card 1: Connecto Platform */}
          <div
            ref={observe}
            className="animate-fade-up col-span-12 min-w-0 lg:col-span-7"
          >
            <Link
              to="/service/platform/"
              className="group block h-full w-full min-w-0 overflow-hidden rounded-2xl p-6 md:p-8 lg:p-10 bg-primary text-white shadow-lg cursor-pointer
                         md:hover:-translate-y-1 md:hover:scale-[1.01] transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 bg-white/15 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <Layers className="w-7 h-7" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-white group-hover:text-primary flex items-center justify-center text-white transition-all duration-300">
                    <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isAr ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                  </div>
                </div>
                <h3 className="font-ibm text-xl md:text-2xl font-bold text-white mb-3 leading-snug">
                  {card1Title}
                </h3>
                <p className="font-almarai text-[15px] md:text-[17px] text-white/90 mb-6 leading-relaxed">
                  {card1Desc}
                </p>
                <ul className="space-y-3.5 mb-8">
                  {card1Feats.map((feat, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/95 font-almarai text-[14px] md:text-[15px]">
                      <CheckCircle className="w-5 h-5 text-accent-orange flex-shrink-0" />
                      <span className="leading-relaxed">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-row-reverse flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/15 lg:flex-row">
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-5 gap-y-3 sm:gap-x-8 sm:gap-y-4 lg:gap-x-10">
                  {marketplaces.map((m, i) => (
                    <img
                      key={i}
                      src={m.src}
                      width={m.width}
                      height={m.height}
                      alt={`${m.name} marketplace`}
                      loading="lazy"
                      className="h-5 w-auto shrink-0 object-contain sm:h-6"
                    />
                  ))}
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold font-almarai text-white/90 group-hover:text-white transition-colors">
                  <span>{isAr ? 'استكشف المنصة' : 'Explore Platform'}</span>
                  <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                </span>
              </div>
            </Link>
          </div>

          {/* Card 2: Account Management */}
          <div
            ref={observe}
            className="animate-fade-up col-span-12 min-w-0 lg:col-span-5"
            style={{ transitionDelay: '0.15s' }}
          >
            <Link
              to="/service/account-management/"
              className="group block h-full w-full min-w-0 overflow-hidden bg-white rounded-2xl shadow-card-soft p-6 md:p-8 lg:p-10 border-b-4 border-accent-orange border-t border-x border-edge-card flex flex-col justify-between
                         cursor-pointer md:hover:-translate-y-1 md:hover:scale-[1.01] transition-all duration-300"
            >
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 bg-accent-orange-bg rounded-xl flex items-center justify-center text-accent-orange flex-shrink-0">
                    <Users className="w-7 h-7" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-surface-section group-hover:bg-accent-orange group-hover:text-white flex items-center justify-center text-heading transition-all duration-300">
                    <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isAr ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                  </div>
                </div>
                <h3 className="font-ibm text-xl md:text-2xl font-bold text-heading mb-3 leading-snug">
                  {card2Title}
                </h3>
                <p className="font-almarai text-[15px] md:text-[17px] text-body-muted mb-6 leading-relaxed">
                  {card2Desc}
                </p>
                <ul className="space-y-3.5 mb-8">
                  {card2Feats.map((feat, i) => (
                    <li key={i} className="flex items-center gap-3 text-heading font-almarai text-[14px] md:text-[15px]">
                      <CheckCircle className="w-5 h-5 text-accent-orange flex-shrink-0" />
                      <span className="leading-relaxed">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-4 border-t border-edge-card flex justify-end">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold font-almarai text-primary group-hover:text-primary-hover transition-colors">
                  <span>{isAr ? 'عرض التفاصيل' : 'View Details'}</span>
                  <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScaleCards;
